import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ShieldCheck, ArrowRight, MessageCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { popularCountries, customsProcess, customsFaqs, contact } from "../mock";

export default function CustomsClearance() {
  return (
    <>
      <Seo
        title="How We Handle Customs Clearance for Medicine Shipments | ShipMeds"
        description="See how ShipMeds clears Ayurvedic & Homeopathy medicine shipments through customs in the USA, UK, Canada, Australia, UAE and 220+ countries - with a 100% clearance guarantee."
        path="/customs-clearance"
      />
      <PageHero title="Customs Clearance, Handled For You" subtitle="India's first medicine courier with a 100% destination customs clearance guarantee on branded & unbranded Ayurvedic and Homeopathy medicines." crumb="Customs Clearance" />

      {/* Guarantee intro */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 font-bold px-4 py-1.5 rounded-full text-xs uppercase tracking-widest mb-4"><ShieldCheck className="w-4 h-4" /> 100% Clearance Guarantee</div>
          <h2 className="text-3xl font-extrabold text-slate-900 mb-4">We Clear Customs at the Destination - Not Just Dispatch From India</h2>
          <p className="text-slate-600 leading-relaxed">Most couriers stop caring once a parcel leaves India. ShipMeds is different. Our documentation desk and destination partners manage clearance at the receiving country's customs authority, so your medicines are never stuck at the border. If any duty applies, only the receiver pays it directly to authorities.</p>
        </div>
      </section>

      {/* Process */}
      <section className="py-14 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl font-extrabold text-slate-900">Our Customs Clearance Process</h2>
            <p className="mt-3 text-slate-600">A consistent, transparent process we follow for every international shipment.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {customsProcess.map((s) => {
              const Icon = Icons[s.icon] || Icons.FileText;
              return (
                <div key={s.no} className="relative bg-white rounded-2xl p-6 border border-slate-100 card-shadow">
                  <span className="absolute top-4 right-4 text-3xl font-extrabold text-slate-100">{s.no}</span>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-blue-600 text-white flex items-center justify-center mb-4"><Icon className="w-6 h-6" /></div>
                  <h4 className="font-bold text-slate-900 mb-2 leading-snug">{s.title}</h4>
                  <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Country grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl font-extrabold text-slate-900">Customs Clearance by Country</h2>
            <p className="mt-3 text-slate-600">Explore how we clear your medicines through customs in each destination.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {popularCountries.map((c) => (
              <Link key={c.slug} to={`/customs-clearance/${c.slug}`} className="group flex items-center justify-between bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-teal-300 hover:bg-teal-50/40 transition-colors">
                <span className="flex items-center gap-3">
                  <img src={`https://flagcdn.com/w80/${c.code}.png`} alt={c.name} className="w-10 h-7 object-cover rounded-md shadow-sm" />
                  <span className="font-semibold text-slate-800 group-hover:text-teal-600 transition-colors">Clearance in {c.name}</span>
                </span>
                <ArrowRight className="w-5 h-5 text-teal-500 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-extrabold text-slate-900 text-center mb-8">Customs Clearance FAQs</h2>
          <div className="space-y-4">
            {customsFaqs.map((f) => (
              <div key={f.q} className="bg-white rounded-2xl p-6 border border-slate-100">
                <h4 className="font-bold text-slate-900 mb-2">{f.q}</h4>
                <p className="text-sm text-slate-500 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-gradient-to-r from-teal-700 to-teal-600 rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-2">Worried about customs? Don't be.</h3>
            <p className="text-teal-100 mb-5">Talk to our clearance experts and ship with a 100% guarantee.</p>
            <a href={contact.whatsapp} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-3 rounded-lg transition-colors"><MessageCircle className="w-5 h-5" /> WhatsApp Us</a>
          </div>
        </div>
      </section>
    </>
  );
}
